/**
 * SignaturePadControl
 * Digital signature drawing pad for form input
 * Uses signature_pad library for canvas-based drawing
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import type { ControlRendererProps } from '../types';
import { UndoIcon, CloseIcon } from '@atlas-forms/ui-components-react';

const SignaturePadControl = memo<ControlRendererProps>(({ control, value, onChange, disabled, mode }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize signature pad
  useEffect(() => {
    if (!canvasRef.current || disabled || mode === 'view') return;

    try {
      // Dynamically import signature_pad
      const SignaturePadLib = require('signature_pad');
      const canvas = canvasRef.current;

      // Set canvas size
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        }
      }

      // Initialize signature pad
      signaturePadRef.current = new SignaturePadLib(canvas, {
        minWidth: 0.5,
        maxWidth: 2.5,
        penColor: '#1e293b',
        backgroundColor: '#ffffff',
      });

      // Load existing signature if present
      if (typeof value === 'string' && value.startsWith('data:') && signaturePadRef.current) {
        signaturePadRef.current.fromDataURL(value);
      }

      setError(null);
    } catch (err) {
      setError('Signature pad library unavailable');
      console.error('Failed to load signature_pad:', err);
    }
  }, [disabled, mode]);

  // Emit data on signature end
  useEffect(() => {
    if (!signaturePadRef.current || disabled || mode === 'view') return;

    const handleEnd = () => {
      const dataUrl = signaturePadRef.current?.toDataURL();
      if (dataUrl) {
        onChange?.(dataUrl);
      }
    };

    signaturePadRef.current.addEventListener('endStroke', handleEnd);

    return () => {
      signaturePadRef.current?.removeEventListener('endStroke', handleEnd);
    };
  }, [onChange, disabled, mode]);

  const handleClear = useCallback(() => {
    if (signaturePadRef.current && !disabled) {
      signaturePadRef.current.clear();
      onChange?.('');
    }
  }, [onChange, disabled]);

  const handleUndo = useCallback(() => {
    if (signaturePadRef.current && !disabled) {
      const data = signaturePadRef.current.toData();
      if (data.length > 0) {
        data.pop();
        signaturePadRef.current.fromData(data);
        onChange?.(signaturePadRef.current.toDataURL());
      }
    }
  }, [onChange, disabled]);

  // View mode: display signature image
  if (mode === 'view') {
    return (
      <div className="af-signature-pad__container af-signature-pad--view">
        {control.label && <div className="af-control__label">{control.label}</div>}
        {typeof value === 'string' && value.startsWith('data:') ? (
          <img src={value} alt="Signature" className="af-signature-pad__image" />
        ) : (
          <div className="af-signature-pad__placeholder">No signature</div>
        )}
      </div>
    );
  }

  return (
    <div className="af-signature-pad__container" data-control-id={control.id}>
      {control.label && <label className="af-control__label">{control.label}</label>}

      {error ? (
        <div className="af-control__error-message" role="alert">
          {error}
        </div>
      ) : (
        <>
          <canvas
            ref={canvasRef}
            className="af-signature-pad__canvas"
            style={{
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              cursor: disabled ? 'not-allowed' : 'crosshair',
              opacity: disabled ? 0.6 : 1,
              display: 'block',
              width: '100%',
              height: '200px',
            }}
            aria-label={control.label || 'Signature pad'}
          />

          <div className="af-signature-pad__buttons">
            <button
              type="button"
              onClick={handleUndo}
              disabled={disabled}
              className="af-button af-button--secondary"
              title="Undo last stroke"
            >
              <UndoIcon width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Undo
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="af-button af-button--secondary"
              title="Clear signature"
            >
              <CloseIcon width={14} height={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Clear
            </button>
          </div>
        </>
      )}

      {control.helpText && <div className="af-control__help">{control.helpText}</div>}
    </div>
  );
});

SignaturePadControl.displayName = 'SignaturePadControl';

export default SignaturePadControl;
